import * as React from "react";
import { renderToString } from "react-dom/server";
import {
    IDocument,
    ISpread,
    IPage,
    IDisclaimer,
    IDivider,
    IPdfAnnotation,
    IPlaceHolder,
} from "src/infrastructure/interfaces";
import ResultContent from "../components/Editor/components/Result/ResultContent";
import { LayoutType } from 'src/infrastructure/LayoutTypes';
import { placeholderTypes } from 'src/infrastructure/placeholderTypes';

const template = `
<html>
  <head>
    <style id="styles"></style>
    <link href="https://use.fontawesome.com/releases/v5.0.6/css/all.css" rel="stylesheet">
  </head>
  <body>
    <div id="content"></div>
  </body>
</html>`;

function renderDocumentToString(document: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[], styles: string, isPrintDownload: boolean): string {
    let resultContentString: string;

    switch (document.type) {
        case "Catalog":
            resultContentString = renderCatalogAsString(document, dividers, disclaimers, isPrintDownload);
            break;
        case "Brochure":
            resultContentString = renderBrochureAsString(document, dividers, disclaimers, isPrintDownload);
            break;
        case "Bulletin":
            resultContentString = renderBulletinAsString(document, dividers, disclaimers, isPrintDownload);
            break;
        case "Postcard":
            resultContentString = renderPostcardAsString(document, dividers, disclaimers, isPrintDownload);
            break;
        case "Poster":
            resultContentString = renderPosterAsString(document, dividers, disclaimers, isPrintDownload);
            break;
        default:
            throw new Error(
                `${document.type} is not a valid type for rendering as a string.`
            );
    }

    const finalResultContentString = replaceStaticAssetPaths(
        resultContentString
    );
    const stylesReplaced = styles
      .replace(/background-image:url\('\/Images\//g, "background-image:url(\'file:\/\/\/home\/pgbadmin\/pgbserver\/pdfAssets\/img\/");

    const html = template
        .replace('<div id="content"></div>', finalResultContentString)
        .replace('<style id="styles"></style>', stylesReplaced)
        .replace(/https:\/\/lovelandguidebuilder\.com\//g, 'file:\/\/\/var\/www\/pgbclient\/build\/');

    return html;
}

function renderResultContentToString({
    document,
    selectedSpread,
    selectedPage,
    disclaimers,
    dividers,
    isSpreadView,
    isPrintDownload
}: {
    document: IDocument;
    selectedSpread: ISpread | null;
    selectedPage: IPage | null;
    disclaimers: any;
    dividers: IDivider[];
    isSpreadView: boolean;
    isPrintDownload: boolean;
}) {
    const component = React.createElement(ResultContent, {
        document,
        selectedSpread,
        selectedPage,
        isSpreadView,
        disclaimers,
        dividers,
        isEditorEditing: false,
        isWebPageCover: false,
        isPrintDownload
    });

    const result = renderToString(component);

    return result;
}

function renderCatalogAsString(document: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[], isPrintDownload: boolean): string {
    const pages = [];

    // Render the title page - First spread. Right page.
    pages.push(
        renderResultContentToString({
            document,
            selectedSpread: document.spreads[0],
            selectedPage: document.spreads[0].right,
            disclaimers,
            isSpreadView: false,
            dividers,
            isPrintDownload
        })
    );

    // Loop through all the spreads (left and right page tuples)
    for (
        let spreadIndex = 1;
        spreadIndex < document.spreads.length;
        spreadIndex += 1
    ) {
        // Get the current spread
        const spread = document.spreads[spreadIndex];

        // Left Page
        if (spread.left) {
            pages.push(
                renderResultContentToString({
                    document,
                    selectedSpread: spread,
                    selectedPage: spread.left,
                    disclaimers,
                    isSpreadView: false,
                    dividers,
                    isPrintDownload
                })
            );
        }

        // Right page
        if (spread.right) {
            // If this is an agronomy spread, duplicate the agronomy seeds from the left page
            const cachedRightPlaceholders = spread.right.placeholders;
            if (spread.right.layout === LayoutType.Agronomy) {
                spread.right.placeholders = spread.left.placeholders.slice(0, 16).map((ph: IPlaceHolder) => {
                    ph.type = placeholderTypes.Agronomy;
                    return ph;
                }).concat(spread.right.placeholders);
            }
            // Create the react component and add it to the spread array
            pages.push(
                renderResultContentToString({
                    document,
                    selectedSpread: spread,
                    selectedPage: spread.right,
                    disclaimers,
                    isSpreadView: false,
                    dividers,
                    isPrintDownload
                })
            );
            spread.right.placeholders = cachedRightPlaceholders;
        }
    }

    // Render the last page - First Spread. Left page.
    pages.push(
        renderResultContentToString({
            document,
            selectedSpread: document.spreads[0],
            selectedPage: document.spreads[0].left,
            disclaimers: null,
            isSpreadView: false,
            dividers,
            isPrintDownload
        })
    );

    return pages.join("");
}

function renderBulletinAsString(document: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[], isPrintDownload: boolean): string {
  const pages = document.pages.map(page => {
    return renderResultContentToString({
      document,
      selectedSpread: null,
      selectedPage: page,
      disclaimers,
      isSpreadView: false,
      dividers,
      isPrintDownload
    })
  });

  return pages.join('');
}

function renderBrochureAsString(document: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[], isPrintDownload: boolean): string {
  const spreads = document.spreads.map(spread => {
    return renderResultContentToString({
      document,
      selectedSpread: spread,
      selectedPage: null,
      disclaimers,
      isSpreadView: true,
      dividers,
      isPrintDownload
    });
  });

  return spreads.join('');
}
function renderPostcardAsString(document: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[], isPrintDownload: boolean): string {

    const spreads = document.spreads.map(spread => {

      return renderResultContentToString({
        document,
        selectedSpread: spread,
        selectedPage: null,
        disclaimers,
        isSpreadView: true,
        dividers,
        isPrintDownload
      })
    });

    return spreads.join('');
}

function renderPosterAsString(document: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[], isPrintDownload: boolean): string {

    return renderResultContentToString({
        document,
        selectedSpread: document.spreads[0],
        selectedPage: null,
        disclaimers,
        isSpreadView: true,
        dividers: dividers,
        isPrintDownload
    });
}

function replaceStaticAssetPaths(content: string): string {
    const clientImagesFolder = process.env.REACT_APP_PUBLIC_IMAGES_URL;

    const pdfConfig = {
        svg: "",
        fonts: "",
    };

    return (
        content
            .replace(/\/static/g, "static")
            .replace(/\/images/g, "images")
            .replace(/&quot;/g, "'")
            .replace(/background-image:url\('images\//g, "background-image:url('" + clientImagesFolder)
            .replace(/src="images\//g, 'src="' + clientImagesFolder)
            .replace(/data="images\//g, 'data="' + clientImagesFolder)
            .replace(/: url\(..\/img/g, ": url(" + pdfConfig.svg)
            .replace('.svg)"', ".svg')\"")
            .replace(/PDF_FONT_PATH/g, pdfConfig.fonts)
            .replace(/PDF_IMAGE_PATH/g, pdfConfig.svg)
    );
}


function getDocumentLinkAnnotations(document: IDocument): IPdfAnnotation[] {
    if ((document.type != "Bulletin") && (document.type != "Catalog")) {
        return [];
    }
    
    var annotations: IPdfAnnotation[] = document.pages
    .filter((page) => {
        return ((page.placeholders != null)
        && (page.placeholders.length > 0)
        && (page.placeholders[0].data != null)
        && (page.placeholders[0].data != null)
        && (page.placeholders[0].data["url"] != null)
        && (page.placeholders[0].data["url"].length > 0));
    })
    .map((page) => {
        let position = [0, 0, 615, 795];
        if (document.type == "Bulletin") {
            position = [425, 125, 150, 20];
        } else {
            // Catalog
            if ((page.placeholders[0]!.data!["type"] != null)
                && (page.placeholders[0]!.data!["type"].toUpperCase() == "CATALOG")) {
                // Loveland product
                position = [25, 50, 220, 20];
            } else {
                // IBlank
                position = [0, 0, 615, 795];
            }
        }
        return {
            page: page.number - 1,
            position,
            content: page.placeholders[0]!.data!["url"]
        }
    });

    // Add social
    if (document.type == "Bulletin") {
        const socialLeft = 37;
        const socialTop = 65;
        const socialWidth = 110;
        const socialHeight = 20;
        const socialPadding = 7;
        const socialDistanceFromStartToStart = socialWidth + socialPadding;

        const facebookLink = {
            page: 0,
            position: [socialLeft, socialTop, socialWidth, socialHeight],
            content: "http://lpi.ag/fbbuilder",
        }
        const twitterLink = {
            page: 0,
            position: [(socialLeft + socialDistanceFromStartToStart), socialTop, socialWidth, socialHeight],
            content: "http://lpi.ag/twitterbuilder",
        }
        const instagramLink = {
            page: 0,
            position: [socialLeft + (2 * socialDistanceFromStartToStart), socialTop, socialWidth, socialHeight],
            content: "http://lpi.ag/instabuilder"
        }
        annotations = annotations.concat([facebookLink, twitterLink, instagramLink]);
    }

    return annotations;
}

export { renderDocumentToString, getDocumentLinkAnnotations };



// WEBPACK FOOTER //
// ./src/services/RenderingService.tsx